import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Loader2, Route, InfoIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import TaxiStatus from "./TaxiStatus";
import VehicleInfoDialog from "./VehicleInfoDialog";
import clsx from "clsx";
import { meters_to_km_velocity } from "@/utils/variables";
import { Vehicle } from "@/interface/entityInterface";

interface Props {
  data: Vehicle[];
  isLoading: boolean;
  textSearch: any;
  filterStatus: any;
  setSelectedVehicleOnList: any;
  selectedVehicleList: any;
  setRouteOpen: Dispatch<SetStateAction<boolean>>;
  setShowList: Dispatch<SetStateAction<boolean>>;
}

const Vehicles: FC<Props> = ({
  data,
  isLoading,
  textSearch,
  filterStatus,
  setSelectedVehicleOnList,
  selectedVehicleList,
  setRouteOpen,
  setShowList
}) => {
  const [width] = useState(window.innerWidth);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [infoVehicle, setInfoVehicle] = useState<any>(null);
  const [variables, setVariables] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<"all" | "active" | "parked">("all");
  const itemRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
  const getVariableValue = (variables: any[], name: string) => {
    const variable = variables.find((v) => v.name === name);
    return variable ? variable.value : null;
  };

  const handleInfoIconClick = (e: React.MouseEvent, vehicle: any) => {
    e.stopPropagation();
    setInfoVehicle(vehicle);
    setVariables(vehicle.gpsData.variables);
  };

  const filteredVehicles = useMemo(() => {
    if (!data) return [];

    return data.filter((vehicle: any) => {
      const ignition = getVariableValue(vehicle.gpsData.variables, "Ignition");
      const nicknameMatch = vehicle.nickname
        ?.toLowerCase()
        ?.includes(searchTerm.toLowerCase());

      if (!nicknameMatch) return false;

      if (selected === "all") {
        return true;
      } else if (selected === "active") {
        // active = ignition === "True"
        return ignition === "True";
      } else if (selected === "parked") {
        // parked = ignition === "False"
        return ignition === "False";
      }

      return true;
    });
  }, [data, searchTerm, selected]);

  useEffect(() => {
    textSearch(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    filterStatus(selected);
  }, [selected]);

  useEffect(() => {
    if (selectedVehicleList) {
      setSelectedVehicle(selectedVehicleList?.nickname);
      if (selectedVehicleList?.id && itemRefs.current[selectedVehicleList.id]) {
        itemRefs.current[selectedVehicleList.id]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [selectedVehicleList]);

  return (
    <div>
      <div className="p-4 border rounded-xl shadow-lg bg-background relative w-[300px]">
          <button
            onClick={() => {
              setSelectedVehicle(null);
              setShowList(false);
              setSelectedVehicleOnList(null);
            }}
            type="button"
            className="cursor-pointer absolute top-3 right-3 text-gray-500 hover:text-red-500"
          >
            <X className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold mb-2">Vehicles List</h3>
          <Input
            type="text"
            placeholder="Search vehicles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-3"
          />
          <div className="mt-[-8px] mb-2">
            <Badge
              variant={selected === "all" ? "default" : "outline"}
              className={clsx("mr-1 cursor-pointer", {
                "bg-gray-600 text-white border-transparent": selected === "all",
              })}
              onClick={() => setSelected("all")}
            >
              All
            </Badge>
            <Badge
              variant={selected === "active" ? "default" : "outline"}
              className={clsx("mr-1 cursor-pointer", {
                "bg-gray-600 text-white border-transparent":
                  selected === "active",
              })}
              onClick={() => setSelected("active")}
            >
              Active
            </Badge>
            <Badge
              variant={selected === "parked" ? "default" : "outline"}
              className={clsx("cursor-pointer", {
                "bg-gray-600 text-white border-transparent":
                  selected === "parked",
              })}
              onClick={() => setSelected("parked")}
            >
              Parked
            </Badge>
          </div>
          <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {isLoading && (
              <div className="flex items-center justify-center h-13">
                <Loader2 className="animate-spin text-gray-500" />
              </div>
            )}
            {filteredVehicles?.map((vehicle) => {
              const vehicleSpeed = Math.round(
                vehicle.gpsData.velocity.groundSpeed * meters_to_km_velocity,
              );
              return (
                <li
                  key={vehicle.id}
                  onClick={() => {
                    setSelectedVehicle(vehicle.nickname);
                    setSelectedVehicleOnList(vehicle);
                  }}
                  ref={(el) => (itemRefs.current[vehicle.id] = el)}
                  className={`flex items-center justify-between py-2 mr-1 rounded-lg cursor-pointer transition hover:bg-muted ${
                    selectedVehicle === vehicle.nickname
                      ? "bg-gray-100 border border-gray-300"
                      : ""
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <TaxiStatus
                      data={vehicle.gpsData}
                      fuelTypes={vehicle.vehicle_tanks}
                    />
                    <span className="text-sm">{vehicle.nickname}</span>
                  </div>
                  <div
                    className={`flex items-center space-x-2 text-sm ${vehicleSpeed === 0 ? "text-gray-500" : vehicleSpeed <= 100 ? "text-green-600" : "text-red-600"}`}
                  >
                    <span>{vehicleSpeed} km/h</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div title="Vehicle Info">
                      <InfoIcon
                        className="w-4 h-4 cursor-pointer"
                        onClick={(e) => handleInfoIconClick(e, vehicle)}
                      />
                    </div>
                    <Button
                      className="w-4 h-4 p-1"
                      variant="outline"
                      onClick={() => setRouteOpen((prev) => !prev)}
                    >
                      <Route className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

      {/* Vehicle Information Display */}
      <VehicleInfoDialog
        infoVehicle={infoVehicle}
        isOpen={!!infoVehicle}
        onClose={() => setInfoVehicle(null)}
        variables={variables}
      />
    </div>
  );
};

export default Vehicles;
